import React, { useEffect, useState } from "react";
import { Save, Loader2, KeyRound } from "lucide-react";
import { fetchSettings, patchSettings, changePassword } from "../../lib/api";
import { useSite } from "../../context/SiteContext";
import ImageUpload from "../ImageUpload";
import { toast } from "../../hooks/use-toast";

const input = "w-full bg-navy-700 border border-white/15 focus:border-gold rounded-lg px-3 py-2 text-white text-sm placeholder-slate-500 outline-none transition-colors";
const label = "block text-xs text-slate-400 mb-1.5";
const card = "bg-navy-800 rounded-2xl border border-white/10 p-6";

const StorePanel = () => {
  const { reload } = useSite();
  const [s, setS] = useState(null);
  const [saving, setSaving] = useState(false);
  const [pw, setPw] = useState({ current: "", next: "" });
  const [pwBusy, setPwBusy] = useState(false);

  useEffect(() => { fetchSettings().then(setS).catch(() => {}); }, []);
  if (!s) return <div className="text-slate-400">Loading...</div>;

  const set = (k, v) => setS((p) => ({ ...p, [k]: v }));
  const setContact = (k, v) => setS((p) => ({ ...p, contact: { ...p.contact, [k]: v } }));

  const save = async () => {
    setSaving(true);
    try { await patchSettings(s); await reload(); toast({ title: "Saved", description: "Store settings updated." }); }
    catch { toast({ title: "Error", description: "Could not save." }); }
    finally { setSaving(false); }
  };

  const savePw = async () => {
    if (!pw.current || pw.next.length < 6) { toast({ title: "Check password", description: "New password must be at least 6 characters." }); return; }
    setPwBusy(true);
    try { await changePassword(pw.current, pw.next); setPw({ current: "", next: "" }); toast({ title: "Password changed", description: "Use your new password next time." }); }
    catch (e) { toast({ title: "Error", description: e?.response?.data?.detail || "Could not change password." }); }
    finally { setPwBusy(false); }
  };

  return (
    <div className="space-y-6">
      <div className={card}>
        <h3 className="text-white font-bold text-lg mb-5">Brand</h3>
        <div className="grid sm:grid-cols-2 gap-4">
          <div><label className={label}>Brand Name</label><input className={input} value={s.brandName || ""} onChange={(e) => set("brandName", e.target.value)} /></div>
          <div><label className={label}>Logo Text (fallback)</label><input className={input} value={s.logoText || ""} onChange={(e) => set("logoText", e.target.value)} /></div>
          <div className="sm:col-span-2"><label className={label}>Logo Image (optional — overrides logo text)</label><ImageUpload value={s.logoImage} onUpload={(url) => set("logoImage", url)} onClear={() => set("logoImage", "")} label="Upload logo" /></div>
          <div className="sm:col-span-2"><label className={label}>Tagline</label><input className={input} value={s.tagline || ""} onChange={(e) => set("tagline", e.target.value)} /></div>
          <div className="sm:col-span-2"><label className={label}>Announcement Bar</label><input className={input} value={s.announcement || ""} onChange={(e) => set("announcement", e.target.value)} /></div>
          <div><label className={label}>Rating</label><input className={input} value={s.rating || ""} onChange={(e) => set("rating", e.target.value)} /></div>
          <div><label className={label}>Review Count</label><input className={input} value={s.reviewCount || ""} onChange={(e) => set("reviewCount", e.target.value)} /></div>
        </div>
      </div>

      <div className={card}>
        <h3 className="text-white font-bold text-lg mb-5">Hero Section</h3>
        <div className="grid sm:grid-cols-2 gap-4">
          <div><label className={label}>Hero Title</label><input className={input} value={s.heroTitle || ""} onChange={(e) => set("heroTitle", e.target.value)} /></div>
          <div><label className={label}>Highlight (gold part of title)</label><input className={input} value={s.heroHighlight || ""} onChange={(e) => set("heroHighlight", e.target.value)} /></div>
          <div className="sm:col-span-2"><label className={label}>Hero Subtitle</label><textarea className={`${input} h-20 resize-none`} value={s.heroSubtitle || ""} onChange={(e) => set("heroSubtitle", e.target.value)} /></div>
          <div><label className={label}>CTA Button Text</label><input className={input} value={s.heroCtaText || ""} onChange={(e) => set("heroCtaText", e.target.value)} /></div>
          <div><label className={label}>Hero Image</label><ImageUpload value={s.heroImage} onUpload={(url) => set("heroImage", url)} label="Upload hero image" /></div>
        </div>
      </div>

      <div className={card}>
        <h3 className="text-white font-bold text-lg mb-5">Contact</h3>
        <div className="grid sm:grid-cols-2 gap-4">
          <div><label className={label}>Telegram Handle</label><input className={input} value={s.contact?.telegramHandle || ""} onChange={(e) => setContact("telegramHandle", e.target.value)} /></div>
          <div><label className={label}>Telegram Link</label><input className={input} value={s.contact?.telegramLink || ""} onChange={(e) => setContact("telegramLink", e.target.value)} /></div>
          <div><label className={label}>Support Email</label><input className={input} value={s.contact?.supportEmail || ""} onChange={(e) => setContact("supportEmail", e.target.value)} /></div>
          <div><label className={label}>Support Hours</label><input className={input} value={s.contact?.hours || ""} onChange={(e) => setContact("hours", e.target.value)} /></div>
        </div>
      </div>

      <div className={card}>
        <h3 className="text-white font-bold text-lg mb-5">Footer &amp; Disclaimer</h3>
        <div className="space-y-4">
          <div><label className={label}>Footer About Text</label><textarea className={`${input} h-20 resize-none`} value={s.footerAbout || ""} onChange={(e) => set("footerAbout", e.target.value)} /></div>
          <div><label className={label}>Disclaimer</label><textarea className={`${input} h-24 resize-none`} value={s.disclaimer || ""} onChange={(e) => set("disclaimer", e.target.value)} /></div>
        </div>
      </div>

      <button onClick={save} disabled={saving} className="inline-flex items-center gap-2 bg-gold hover:bg-gold-light text-navy font-semibold px-6 py-3 rounded-xl transition-colors disabled:opacity-60">{saving ? <Loader2 size={16} className="animate-spin" /> : <Save size={16} />} Save Changes</button>

      <div className={card}>
        <h3 className="text-white font-bold text-lg mb-5 flex items-center gap-2"><KeyRound size={18} className="text-gold" /> Change Admin Password</h3>
        <div className="grid sm:grid-cols-2 gap-4 max-w-xl">
          <div><label className={label}>Current Password</label><input type="password" className={input} value={pw.current} onChange={(e) => setPw({ ...pw, current: e.target.value })} /></div>
          <div><label className={label}>New Password</label><input type="password" className={input} value={pw.next} onChange={(e) => setPw({ ...pw, next: e.target.value })} /></div>
        </div>
        <button onClick={savePw} disabled={pwBusy} className="mt-4 inline-flex items-center gap-2 border border-gold/30 hover:border-gold text-white px-5 py-2.5 rounded-xl transition-colors disabled:opacity-60">{pwBusy ? <Loader2 size={15} className="animate-spin" /> : <KeyRound size={15} />} Update Password</button>
      </div>
    </div>
  );
};

export default StorePanel;
